/**
 * Esquema Zod del formulario de Pedidos de láminas (alta única + edición).
 *
 * Valida la entrada del formulario ANTES de llamar a la API (`docs/
 * frontend_conventions.md`, Validación). El tipo del formulario se **infiere** del
 * esquema (`z.infer`), no se escribe a mano. Las reglas replican las del
 * `PedidoLaminaService` (`pedidos_lamina_service.py`, `_agregar_unico`/`editar`):
 *
 *  - categoría obligatoria;
 *  - calibre obligatorio si la categoría lo lleva (`PEDIDOS_CALIBRES`);
 *  - medida obligatoria si la categoría la lleva (`PEDIDOS_CON_MEDIDA`);
 *  - tubo obligatorio si la categoría es "Tubos";
 *  - descripción obligatoria si NO hay medida estándar (medida vacía o "otra");
 *  - cantidad entero positivo.
 *
 * El formulario trabaja en formato de UI (cantidad como texto); la capa `api.ts`
 * la pasa como string al backend (que la trata como texto de formulario).
 */
import { z } from "zod";

import {
  categoriaEsTubo,
  categoriaTieneCalibre,
  categoriaTieneMedida,
} from "./constants";

/** Campo de cantidad en texto: obligatorio, entero positivo. */
const cantidadField = z
  .string()
  .trim()
  .min(1, "Indica una cantidad válida")
  .regex(/^\d+$/, "Indica una cantidad válida")
  .refine((value) => Number(value) > 0, "Indica una cantidad válida");

/** Esquema base del formulario (sin las reglas cruzadas por categoría). */
const pedidoFormBase = z.object({
  categoria: z.string().trim().min(1, "Selecciona una categoría"),
  calibre: z.string().trim().optional().default(""),
  medida: z.string().trim().optional().default(""),
  tubo: z.string().trim().optional().default(""),
  descripcion: z.string().trim().optional().default(""),
  cantidad: cantidadField,
});

/**
 * Esquema completo: base + reglas cruzadas que dependen de la categoría (mismo
 * orden de validación que el service). Cada error se ancla a su campo.
 */
export const pedidoFormSchema = pedidoFormBase.superRefine((values, ctx) => {
  const categoria = values.categoria.trim();
  if (!categoria) {
    return;
  }
  const medida = (values.medida ?? "").trim();
  const tieneMedidaEstandar = Boolean(medida && medida !== "otra");

  if (categoriaTieneCalibre(categoria) && !(values.calibre ?? "").trim()) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["calibre"],
      message: "Indica el calibre",
    });
  }
  if (categoriaTieneMedida(categoria) && !medida) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["medida"],
      message: "Selecciona la medida de la lámina",
    });
  }
  if (categoriaEsTubo(categoria) && !(values.tubo ?? "").trim()) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["tubo"],
      message: "Selecciona la medida del tubo",
    });
  }
  if (!tieneMedidaEstandar && !(values.descripcion ?? "").trim()) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["descripcion"],
      message: "Escribe una descripción del producto",
    });
  }
});

/** Valores del formulario de pedido (inferidos del esquema). */
export type PedidoFormValues = z.infer<typeof pedidoFormSchema>;
